import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import type { ToastState } from "../../ui/Toast/Toast";
import GuardianWorkspace from "../../guardian/GuardianWorkspace";
import { guardianApi } from "../../../lib/guardian";
import type { GuardianRequestDecision, GuardianRequestResponse } from "../../../types/guardianTypes";
import { apiErrorToMessage } from "../../../utils/ApiUtils";
import { mapActiveGuardians, mapIncomingGuardianRequests, mapOutgoingGuardianRequests } from "../../guardian/utils";

const TeenGuardianWorkspace = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeGuardians, setActiveGuardians] = useState<GuardianRequestResponse[]>([]);
  const [incomingRequests, setIncomingRequests] = useState<GuardianRequestResponse[]>([]);
  const [outgoingRequests, setOutgoingRequests] = useState<GuardianRequestResponse[]>([]);
  const [targetEmail, setTargetEmail] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [decidingKey, setDecidingKey] = useState<string | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);
  const [composerError, setComposerError] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastState>(null);
  const setupReason = searchParams.get("setup");
  const returnTo = searchParams.get("returnTo") ?? "/gigs";

  const refresh = async () => {
    setIsLoading(true);
    setPageError(null);
    try {
      const [incoming, outgoing] = await Promise.all([guardianApi.listTeenIncoming(), guardianApi.listTeenOutgoing()]);

      const combinedByGuardianId = new Map<string, GuardianRequestResponse>();
      [...incoming, ...outgoing].forEach((request) => {
        const requestKey = request.guardianUserId ?? request.targetEmail ?? `${request.status}-${request.updatedAt}`;
        combinedByGuardianId.set(requestKey, request);
      });

      setActiveGuardians(Array.from(combinedByGuardianId.values()).filter((request) => request.status === "ACTIVE"));
      setIncomingRequests(incoming.filter((request) => request.status === "PENDING"));
      setOutgoingRequests(outgoing.filter((request) => request.status === "PENDING"));

      const guardianRequestGuardianId = searchParams.get("guardianRequest");
      if (guardianRequestGuardianId) {
        const matchingRequest = incoming.find((request) => request.guardianUserId === guardianRequestGuardianId && request.status === "PENDING");
        if (matchingRequest) {
          const guardianName = [matchingRequest.guardianUser?.firstName, matchingRequest.guardianUser?.lastName].filter(Boolean).join(" ").trim();
          setToast({
            tone: "success",
            message: `${guardianName || "That adult"} is waiting in your incoming guardian requests.`,
          });
        }

        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete("guardianRequest");
        setSearchParams(nextParams, { replace: true });
      }
    } catch (requestError) {
      setPageError(apiErrorToMessage(requestError, "Failed to load guardian data."));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
  }, []);

  const handleCreateRequest = async (event: FormEvent) => {
    event.preventDefault();
    if (!targetEmail.trim()) {
      return;
    }

    setSubmitting(true);
    setComposerError(null);
    try {
      const response = await guardianApi.createRequest({
        targetEmail: targetEmail.trim(),
        actingAsRole: "TEEN",
      });
      setTargetEmail("");
      await refresh();
      setToast({
        tone: "success",
        message: response.pendingSignup ? "Signup invite sent to that guardian." : "Guardian request sent.",
      });
    } catch (requestError) {
      const message = apiErrorToMessage(requestError, "Failed to send guardian request.");
      setComposerError(message);
      setToast({ tone: "error", message });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDecision = async (request: GuardianRequestResponse, decision: GuardianRequestDecision) => {
    const key = `${request.guardianUserId}-${decision}`;
    setDecidingKey(key);
    setPageError(null);
    if (!request.guardianUserId) {
      const message = "That guardian request is missing the guardian account.";
      setPageError(message);
      setToast({ tone: "error", message });
      setDecidingKey(null);
      return;
    }
    try {
      await guardianApi.decideRequest(request.guardianUserId, {
        decision,
        actingAsRole: "TEEN",
      });
      await refresh();
      setToast({
        tone: "success",
        message: decision === "ACCEPTED" ? "Guardian request accepted." : "Guardian request declined.",
      });
    } catch (requestError) {
      const message = apiErrorToMessage(requestError, "Failed to update guardian request.");
      setPageError(message);
      setToast({ tone: "error", message });
    } finally {
      setDecidingKey(null);
    }
  };

  const hasActiveGuardians = activeGuardians.length > 0;

  const setupBanner =
    setupReason === "before-apply" ? (
      <div className="rounded-[1.75rem] border border-amber-200 bg-amber-50 px-6 py-5 text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-800">Required Before Applying</p>
        <h2 className="mt-2 text-2xl font-semibold tracking-tight text-amber-950">
          {hasActiveGuardians ? "Parent or guardian connected" : "Connect a parent or guardian first"}
        </h2>
        <p className="mx-auto mt-3 max-w-3xl text-sm leading-6 text-amber-900/85">
          {hasActiveGuardians
            ? "You have an active parent or guardian on this account, so you can go back and apply."
            : "Teens under 18 need at least one active parent or guardian on the account before applying for gigs."}
          {outgoingRequests.length > 0 && !hasActiveGuardians
            ? " You already have a pending guardian request out. Once it is accepted, you can go back and apply."
            : null}
          {outgoingRequests.length === 0 && !hasActiveGuardians
            ? " Send a request here or accept an incoming guardian request to unlock gig applications."
            : null}
        </p>
        {hasActiveGuardians ? (
          <div className="mt-4 flex justify-center">
            <Link
              to={returnTo}
              className="inline-flex items-center justify-center rounded-xl border border-myteengig-primary bg-myteengig-primary px-5 py-3 text-sm font-semibold text-white transition-opacity hover:opacity-90"
            >
              Back to gig applications
            </Link>
          </div>
        ) : null}
      </div>
    ) : null;

  const incomingSection = useMemo(
    () => ({
      title: "Incoming Requests",
      subtitle: "Adults asking to become your guardian",
      emptyMessage: "No incoming guardian requests.",
      items: mapIncomingGuardianRequests(incomingRequests).map((request) => ({
        ...request,
        actions: [
          {
            label: "Accept Request",
            variant: "primary" as const,
            pending: decidingKey === `${request.id}-ACCEPTED`,
            onClick: () => {
              const match = incomingRequests.find((item) => item.guardianUserId === request.id);
              if (match) {
                void handleDecision(match, "ACCEPTED");
              }
            },
          },
          {
            label: "Decline Request",
            variant: "outline" as const,
            pending: decidingKey === `${request.id}-REJECTED`,
            onClick: () => {
              const match = incomingRequests.find((item) => item.guardianUserId === request.id);
              if (match) {
                void handleDecision(match, "REJECTED");
              }
            },
          },
        ],
      })),
    }),
    [decidingKey, incomingRequests],
  );

  const outgoingSection = useMemo(
    () => ({
      title: "Sent Requests",
      subtitle: "Guardian requests you already sent",
      emptyMessage: "You have not sent any guardian requests yet.",
      items: mapOutgoingGuardianRequests(outgoingRequests),
    }),
    [outgoingRequests],
  );

  return (
    <GuardianWorkspace
      backButtonLabel="Back to Dashboard"
      backButtonFallbackTo="/dashboard"
      heroEyebrow="Teen Guardian Workspace"
      heroTitle="Build your guardian circle"
      heroDescription="Invite adults who should oversee your account, respond to guardian requests that came to you, and keep track of who is currently active."
      isLoading={isLoading}
      pageError={pageError}
      toast={toast}
      onDismissToast={() => setToast(null)}
      composer={{
        eyebrow: "Send A Request",
        title: "Invite a parent or guardian",
        description: "Send a connection request to an adult who should be able to oversee your account and approve future gigs when needed.",
        placeholder: "guardian@example.com",
        submitLabel: "Send Request",
      }}
      targetEmail={targetEmail}
      composerError={composerError}
      submitting={submitting}
      onTargetEmailChange={setTargetEmail}
      onSubmit={handleCreateRequest}
      activeTitle="Active Guardians"
      activeSubtitle="People currently connected to you"
      activeEmptyMessage="No active guardians yet. Send a request to get started."
      activeItems={mapActiveGuardians(activeGuardians)}
      incomingSection={incomingSection}
      outgoingSection={outgoingSection}
      setupBanner={setupBanner}
    />
  );
};

export default TeenGuardianWorkspace;
